import { omit } from "lodash";
import queryString from "query-string";
import { useHistory } from "react-router";
import { z } from "zod";
import { formatCoordinates } from "common/utils/coordinates";
import {
  useCreatePropertyMutation,
  useGetParcelQuery,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import useURLParsing from "../../hooks/useURLParsing";
import { track } from "../../utils/tracking";
import { Button } from "../Common/Buttons/Button";
import Icon, { ICON_COLORS, Icons } from "../Common/Icons";
import { SelectedInput } from "../Maps/Map";
import CreatePropertyWarnings from "./CreatePropertyWarnings";
import PropertyForm, { ActionProps, FormData } from "./PropertyForm";

export const createPropertyParams = z
  .object({
    latitude: z.coerce.number(),
    longitude: z.coerce.number(),
    zoom: z.coerce.number().optional(),
    parcelId: z.string().optional(),
  })
  .strict();

export type CreatePropertyParamsInput = z.input<typeof createPropertyParams>;

const CreateProperty = () => {
  const history = useHistory();
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const parsedParams = useURLParsing({
    routeName: "createProperty",
    parser: createPropertyParams,
  });

  const { parcelId, latitude, longitude, zoom } = parsedParams ?? {
    parcelId: undefined,
    latitude: undefined,
    longitude: undefined,
    zoom: undefined,
  };

  const { data, loading, error } = useGetParcelQuery({
    variables: { parcelId },
    skip: !parcelId,
  });

  const [createProperty, { error: createError, loading: loadingCreate }] =
    useCreatePropertyMutation({
      onCompleted: data => {
        const property = data.createProperty;

        const propertyLocation =
          property.streetAddress ?? formatCoordinates(property);

        addSuccessToast(`Property created at ${propertyLocation}`);

        const query = {
          address: property.standardizedAddress,
          lat: property.latitude,
          lng: property.longitude,
          propertyId: property.id,
          parcelId: property.geocacheParcelId,
        } satisfies SelectedInput;

        track("Property Created", { address: property.standardizedAddress });
        history.replace({
          pathname: "/map",
          search: `?${queryString.stringify(query)}`,
        });
      },
      onError: error => {
        const errorMessage = error.graphQLErrors
          .map(error => error.message)
          .join("\n");

        addErrorToast(errorMessage);
      },
    });

  const onSave = async (data: FormData) => {
    await createProperty({ variables: { data: omit(data, "propertyId") } });
  };

  if (loading)
    return (
      <div style={{ textAlign: "center" }}>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </div>
    );
  if (error) return <em>Problem loading create form.</em>;

  const startingPoint =
    latitude && longitude ?
      { latitude: Number(latitude), longitude: Number(longitude) }
    : null;

  const actions = (props: ActionProps): JSX.Element => {
    return (
      <Button
        size="small"
        variant="primary"
        onPress={() => props.validateInput(onSave)}
        isDisabled={props.disabled}
        isPending={loadingCreate}
        title="Save"
      />
    );
  };

  return (
    <PropertyForm
      actions={actions}
      error={createError?.message ?? null}
      parcel={data?.parcel ?? null}
      point={startingPoint}
      property={null}
      zoom={zoom !== undefined ? Math.round(zoom) : null}
      headerText={"Create new property"}
      title={"Property address"}
      subtitle={
        "Provide a few details on the property's address and select a location on the map."
      }
      warningsComponent={CreatePropertyWarnings}
      canSubmitProperty={false}
      editingProperty={false}
    />
  );
};

export default CreateProperty;
